/**
 * Freeze V2 — Live Embryo Grid (raw HTML table)
 *
 * Lightweight HTML-table replacement for the ag-grid detail grid. Techs are
 * fetched once at the grid level and shared by the Grader/Freeze Tech selects.
 */
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { addToast } from '@/app/slices/toast';
import {
  DataTable,
  EditableCell,
  NUM_INPUT,
  SELECT_INPUT,
  TEXT_INPUT,
} from '@/components/common/data-table';
import type { ColumnDef } from '@/components/common/data-table';
import { EmbryoLink } from '@/components/common/links/EmbryoLink';
import { Button } from '@/components/ui/button';
import type { LiveEmbryoResponse } from '@/services/freeze';

import { useFreezeGrid, useFreezeSelection } from '../../contexts';
import { useFertRowSelection } from '../../hooks/useFertRowSelection';
import { useUpdateEmbryoFieldMutation } from '../../services/freezeApi';
import { caneStyle, freezeMethodStyle } from '../../utils/cellStyles';
import { TOG } from '../../utils/columnDefs/fertilizationCols';
import { FIELD_NAMES } from '../../utils/constants';

type LiveEmbryoGridProps = {
  liveEmbryos: LiveEmbryoResponse[];
  /** Temp embryo ids for the same fert, so parent selection accounts for both tables. */
  siblingTempIds: string[];
  isExport: boolean;
  fertilizationId: string;
  billAccountCaningOption: number | null;
  onRefresh: () => void;
};

export const LiveEmbryoGrid = memo(function LiveEmbryoGrid({
  liveEmbryos,
  siblingTempIds,
  isExport,
  fertilizationId,
  billAccountCaningOption,
  onRefresh,
}: LiveEmbryoGridProps) {
  const reduxDispatch = useDispatch();
  const { isUpdateRunning } = useFreezeGrid();
  const { dispatch, liveEmbryoIds, tempEmbryoIds } = useFreezeSelection();
  const { mutateAsync: updateEmbryo } = useUpdateEmbryoFieldMutation();
  const [isLocked, setIsLocked] = useState(true);
  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });
  const techs = useMemo(
    () =>
      (techsData ?? [])
        .filter((t) => t.id && t.name)
        .map((t) => ({ id: t.id as string, name: t.name as string })),
    [techsData],
  );

  const [rows, setRows] = useState(liveEmbryos);
  useEffect(() => setRows(liveEmbryos), [liveEmbryos]);

  const patch = useCallback(
    (id: string | null, partial: Partial<LiveEmbryoResponse>) => {
      if (!id) return;
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...partial } : r)));
    },
    [],
  );

  const ownIds = useMemo(
    () => rows.map((e) => e.id).filter((id): id is string => !!id),
    [rows],
  );
  const selectedSet = useMemo(() => new Set(liveEmbryoIds), [liveEmbryoIds]);
  const allSelected =
    ownIds.length > 0 && ownIds.every((id) => selectedSet.has(id));

  const syncParentFert = useFertRowSelection({
    fertilizationId,
    ownIds,
    siblingIds: siblingTempIds,
    siblingSelectedIds: tempEmbryoIds,
  });

  const toggleRow = useCallback(
    (id: string, checked: boolean) => {
      const next = checked
        ? [...liveEmbryoIds, id]
        : liveEmbryoIds.filter((x) => x !== id);
      dispatch({ type: 'SET_LIVE_IDS', ids: next });
      syncParentFert(next);
    },
    [liveEmbryoIds, dispatch, syncParentFert],
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      const ownSet = new Set(ownIds);
      const next = checked
        ? Array.from(new Set([...liveEmbryoIds, ...ownIds]))
        : liveEmbryoIds.filter((id) => !ownSet.has(id));
      dispatch({ type: 'SET_LIVE_IDS', ids: next });
      syncParentFert(next);
    },
    [ownIds, liveEmbryoIds, dispatch, syncParentFert],
  );

  const save = useCallback(
    async (id: string | null, field: string, value: string | null) => {
      if (!id) return;
      try {
        await updateEmbryo({ id, field, value });
      } catch (error) {
        reduxDispatch(
          addToast({
            severity: 'error',
            summary: 'Save Failed',
            detail: `Could not save embryo field: ${error}`,
            life: 3000,
          }),
        );
      }
    },
    [updateEmbryo, reduxDispatch],
  );

  const invalid = useCallback(
    (summary: string, detail: string) => {
      reduxDispatch(
        addToast({ severity: 'error', summary, detail, closable: true }),
      );
    },
    [reduxDispatch],
  );

  const editStageGrade = useCallback(
    (
      row: LiveEmbryoResponse,
      field: 'stage' | 'grade',
      value: string,
    ): boolean => {
      if (!value) {
        patch(row.id, { [field]: null });
        save(row.id, field, null);
        return true;
      }
      const n = parseInt(value, 10);
      if (field === 'grade' && (n < 1 || n > 3)) {
        invalid('Invalid Grade', 'Grade must be between 1 and 3');
        return false;
      }
      if (field === 'stage' && (n < 3 || n > 9)) {
        invalid('Invalid Stage', 'Stage must be between 3 and 9');
        return false;
      }
      patch(row.id, { [field]: n });
      save(row.id, field, value);
      if (field === 'grade' && n === 3) {
        patch(row.id, { lowQuality: true });
        save(row.id, FIELD_NAMES.LG, 'true');
      }
      if (field === 'stage' && n >= 8) {
        patch(row.id, { crackedZona: true });
        save(row.id, 'crackedZona', 'true');
      }
      // Parity with v1: filling in stage/grade auto-checks the row so techs
      // don't have to click the checkbox separately.
      if (!selectedSet.has(row.id)) toggleRow(row.id, true);
      return true;
    },
    [patch, save, invalid, selectedSet, toggleRow],
  );

  const editDay = useCallback(
    (row: LiveEmbryoResponse, day: 6.5 | 7, checked: boolean) => {
      patch(row.id, {
        d65: day === 6.5 && checked ? 1 : 0,
        d7: day === 7 && checked ? 1 : 0,
      });
      save(row.id, FIELD_NAMES.FREEZE_TIME, checked ? String(day) : null).then(
        onRefresh,
      );
    },
    [patch, save, onRefresh],
  );

  const editTech = useCallback(
    (
      row: LiveEmbryoResponse,
      kind: 'grader' | 'freezeTech',
      techId: string,
    ) => {
      const tech = techs.find((t) => t.id === techId) ?? null;
      if (kind === 'grader') {
        patch(row.id, {
          grader7Id: tech?.id ?? null,
          grader7Name: tech?.name ?? null,
        });
        save(row.id, FIELD_NAMES.GRADER_7_ID, tech?.id ?? null);
      } else {
        patch(row.id, { freezeTechName: tech?.name ?? null });
        save(row.id, FIELD_NAMES.FREEZE_TECH_ID, tech?.id ?? null);
      }
    },
    [techs, patch, save],
  );

  const editsDisabled = isUpdateRunning;
  const lockedEditsDisabled = isUpdateRunning || isLocked;

  const columns = useMemo<ColumnDef<LiveEmbryoResponse>[]>(
    () => [
      {
        key: 'select',
        width: 36,
        reorderable: false,
        hideable: false,
        chooserLabel: 'Select',
        header: (
          <input
            type="checkbox"
            aria-label="Select all live embryos"
            checked={allSelected}
            onChange={(e) => toggleAll(e.target.checked)}
          />
        ),
        render: (row) => {
          const id = row.id ?? '';
          return (
            <input
              type="checkbox"
              checked={selectedSet.has(id)}
              onChange={(e) => toggleRow(id, e.target.checked)}
            />
          );
        },
      },
      {
        key: 'mating',
        header: 'Mating',
        width: 130,
        hideable: false,
        render: (row) => (
          <EmbryoLink embryoId={row.id ?? ''} embryoName={row.name ?? ''} />
        ),
      },
      {
        key: 'straw',
        header: 'Straw',
        width: 60,
        align: 'right',
        editable: true,
        sortable: true,
        sortValue: (row) => row.strawNumber,
        render: (row) => {
          const id = row.id ?? '';
          return (
            <EditableCell
              value={row.strawNumber}
              type="number"
              className={NUM_INPUT}
              disabled={lockedEditsDisabled}
              onCommit={(v) => {
                patch(id, { strawNumber: v ? Number(v) : null });
                save(id, 'strawNumber', v || null);
              }}
            />
          );
        },
      },
      {
        key: 'stage',
        header: 'Stage',
        width: 60,
        align: 'right',
        editable: true,
        render: (row) => (
          <EditableCell
            value={row.stage}
            type="number"
            className={NUM_INPUT}
            disabled={lockedEditsDisabled}
            onCommit={(v) => editStageGrade(row, 'stage', v)}
          />
        ),
      },
      {
        key: 'grade',
        header: 'Grade',
        width: 60,
        align: 'right',
        editable: true,
        render: (row) => (
          <EditableCell
            value={row.grade}
            type="number"
            className={NUM_INPUT}
            disabled={lockedEditsDisabled}
            onCommit={(v) => editStageGrade(row, 'grade', v)}
          />
        ),
      },
      {
        key: 'lg',
        header: 'LG',
        width: 42,
        align: 'center',
        render: (row) => {
          const id = row.id ?? '';
          return (
            <input
              type="checkbox"
              checked={!!row.lowQuality}
              disabled={editsDisabled}
              onChange={(e) => {
                patch(id, { lowQuality: e.target.checked });
                save(id, FIELD_NAMES.LG, String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'cane',
        header: 'Cane',
        /* Cane codes are base + suffix (J08086-1), and some carry a letter
           (H67567-1A). At text-xs the old 76px left ~50px of usable input
           width -- exactly 8 characters -- so the trailing suffix was scrolled
           out of view. v1 rendered cane as ag-grid text, which wrapped; v2's
           EditableCell is always an <input>, which silently clips instead.
           That is WI 37941 #4: the suffix was never missing, just invisible. */
        width: 96,
        editable: true,
        cellStyle: () => caneStyle(billAccountCaningOption),
        render: (row) => {
          const id = row.id ?? '';
          return (
            <EditableCell
              value={row.cane}
              type="text"
              /* caneStyle bolds the <td>, but Tailwind's `font: inherit` for
                 form controls is scoped to .preflight, which freeze-v2 is not
                 under - so the input kept the UA's normal weight. Bold it
                 directly (Miranda's "bold cane codes" note on WI 37941). */
              className={`${TEXT_INPUT} w-full font-bold`}
              disabled={editsDisabled}
              onCommit={(v) => {
                patch(id, { cane: v || null });
                save(id, 'cane', v || null);
              }}
            />
          );
        },
      },
      {
        key: 'tt',
        header: 'T/T',
        width: 46,
        align: 'center',
        cellStyle: () => (isExport ? { backgroundColor: TOG.red } : undefined),
        render: (row) => {
          const id = row.id ?? '';
          return (
            <input
              type="checkbox"
              checked={!!row.tt}
              disabled={lockedEditsDisabled}
              onChange={(e) => {
                patch(id, { tt: e.target.checked });
                save(id, FIELD_NAMES.TT, String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'cz',
        header: 'CZ',
        width: 42,
        align: 'center',
        render: (row) => {
          const id = row.id ?? '';
          return (
            <input
              type="checkbox"
              checked={!!row.crackedZona}
              disabled={editsDisabled}
              onChange={(e) => {
                patch(id, { crackedZona: e.target.checked });
                save(id, 'crackedZona', String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'owner',
        header: 'Owner',
        width: 110,
        render: (row) => row.ownerName,
      },
      {
        key: 'd65',
        header: '6.5',
        width: 42,
        align: 'center',
        render: (row) => (
          <input
            type="checkbox"
            checked={row.d65 === 1}
            disabled={lockedEditsDisabled}
            onChange={(e) => editDay(row, 6.5, e.target.checked)}
          />
        ),
      },
      {
        key: 'd7',
        header: '7',
        width: 42,
        align: 'center',
        render: (row) => (
          <input
            type="checkbox"
            checked={row.d7 === 1}
            disabled={lockedEditsDisabled}
            onChange={(e) => editDay(row, 7, e.target.checked)}
          />
        ),
      },
      {
        key: 'freezeMethod',
        header: 'Freeze Method',
        width: 120,
        cellStyle: (row) => freezeMethodStyle(row.freezeMethodName),
        render: (row) => row.freezeMethodName,
      },
      {
        key: 'dtPlus',
        header: 'DT+',
        width: 46,
        align: 'center',
        render: (row) => {
          const id = row.id ?? '';
          return (
            <input
              type="checkbox"
              checked={Number(row.dtPlus) === 1}
              disabled={lockedEditsDisabled}
              onChange={(e) => {
                patch(id, { dtPlus: e.target.checked ? '1' : '0' });
                save(id, 'dtPlus', String(e.target.checked));
              }}
            />
          );
        },
      },
      {
        key: 'storageLoc',
        header: 'Storage Loc',
        width: 130,
        render: (row) => row.storageLocationName,
      },
      {
        key: 'grader',
        header: 'Grader',
        width: 110,
        editable: true,
        render: (row) => (
          <select
            className={SELECT_INPUT}
            aria-label="Grader"
            value={row.grader7Id ?? ''}
            disabled={editsDisabled}
            onChange={(e) => editTech(row, 'grader', e.target.value)}
          >
            <option value="">—</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        ),
      },
      {
        key: 'freezeTech',
        header: 'Freeze Tech',
        width: 110,
        editable: true,
        render: (row) => (
          <select
            className={SELECT_INPUT}
            aria-label="Freeze Tech"
            value={techs.find((t) => t.name === row.freezeTechName)?.id ?? ''}
            disabled={editsDisabled}
            onChange={(e) => editTech(row, 'freezeTech', e.target.value)}
          >
            <option value="">—</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        ),
      },
      {
        key: 'freezer',
        header: 'Freezer',
        width: 84,
        editable: true,
        render: (row) => {
          const id = row.id ?? '';
          return (
            <EditableCell
              value={row.freezer}
              type="text"
              className={`${TEXT_INPUT} w-full`}
              disabled={editsDisabled}
              onCommit={(v) => {
                patch(id, { freezer: v || null });
                save(id, 'freezer', v || null);
              }}
            />
          );
        },
      },
    ],
    [
      allSelected,
      selectedSet,
      toggleAll,
      toggleRow,
      editsDisabled,
      lockedEditsDisabled,
      patch,
      save,
      editStageGrade,
      editDay,
      editTech,
      techs,
      billAccountCaningOption,
      isExport,
    ],
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 px-1">
        <span className="text-xs font-semibold text-gray-600">
          Live Embryos ({rows.length})
        </span>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setIsLocked((p) => !p)}
          title={isLocked ? 'Unlock editing' : 'Lock editing'}
        >
          <i
            className={`pi ${isLocked ? 'pi-lock' : 'pi-lock-open'} text-xs`}
          />
          {isLocked ? 'Locked' : 'Unlocked'}
        </Button>
      </div>
      <DataTable
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id ?? ''}
        storageKey="freeze-v2-live-embryo-table"
        reorderable
        columnChooser
      />
    </div>
  );
});
